import { type Ref, useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { replaceDashWithComma } from '../../../util/formatting';

import type { AutocompleteOption, AutocompleteProps } from '../../types';

import { FrontPageModal } from '../frontPageModal/FrontPageModal';

import styles from './Autocomplete.module.css';

import { AutocompleteIcon } from './components/AutocompleteIcon';
import { AutocompleteInputField } from './components/AutocompleteInputField';
import { AutocompleteList } from './components/AutocompleteList';

import { useAutocompleteBlur } from './hooks/useAutocompleteBlur';
import { useAutocompleteData } from './hooks/useAutocompleteData';
import { useAutocompleteKeyDown } from './hooks/useAutocompleteKeyDown';
import { useAutocompleteModal } from './hooks/useAutocompleteModal';
import { useAutocompleteSelection } from './hooks/useAutocompleteSelection';
import { useMobileAutofocus } from './hooks/useMobileAutofocus';

const EMPTY_HEADERS: HeadersInit = Object.freeze({});

interface RenderFieldOptions {
    localId: string;
    opts?: {
        inputRef?: Ref<HTMLInputElement>;
        containerRef?: Ref<HTMLDivElement>;
        autoFocus?: boolean;
    };
}

export const Autocomplete = ({
    id,
    label,
    defaultValue,
    placeholder,
    error,
    url,
    headers = EMPTY_HEADERS,
    mapOptions,
    onSelect,
    minChars = 1,
    vertical,
    departure,
    showDefaultOptionOnFocus = false,
    shouldShowOptionCount = true,
    initialOptions,
    initialOptionsTitle,
    tip,
}: AutocompleteProps) => {
    const [hasUserTyped, setHasUserTyped] = useState(false);
    const prevDefaultValueRef = useRef<string | null | undefined>();

    const {
        text,
        setText,
        options,
        setOptions,
        fetching,
        noResults,
        showDropdown,
        setShowDropdown,
        showLoader,
        handleInputChange,
        clearAll,
        setNoResults,
    } = useAutocompleteData({
        url,
        headers,
        mapOptions,
        minChars,
        onSelect,
        onFetchFinally: () => setSelected(0),
        initialOptions,
    });

    useEffect(() => {
        const defaultValueChanged = prevDefaultValueRef.current !== defaultValue;
        prevDefaultValueRef.current = defaultValue;

        if (defaultValueChanged && !hasUserTyped) {
            setText(defaultValue ?? '');
        }
    }, [defaultValue, hasUserTyped, setText]);

    const showDefaultOption = useMemo(
        () => showDefaultOptionOnFocus && text.length === 0 && showDropdown && !noResults,
        [showDefaultOptionOnFocus, text.length, showDropdown, noResults],
    );

    const showOptions = useMemo(
        () => showDefaultOption || (showDropdown && (options.length > 0 || noResults)),
        [showDefaultOption, showDropdown, options.length, noResults],
    );

    const [selectedOption, setSelectedOption] = useState<AutocompleteOption | null>(null);

    const { isSmallScreen, mobileInputRef, mobileInputContainerRef } = useMobileAutofocus(showDropdown);

    const selectOption = useCallback(
        (option: AutocompleteOption) => {
            setText(option.text);
            setSelectedOption(option);
            setShowDropdown(false);
            onSelect(option);
        },
        [setText, setSelectedOption, setShowDropdown, onSelect],
    );

    const { setSelected, setHovered, clearHovered, activeIndex, activeIndexRef, resetSelection, selectCurrentOption } =
        useAutocompleteSelection({
            options,
            showDefaultOption,
            onSelectOption: selectOption,
            initial: -1,
        });

    const onKeyDown = useAutocompleteKeyDown({
        options,
        activeIndex,
        showDefaultOption,
        showDropdown,
        setSelected,
        clearHovered,
        setOptions,
        setNoResults,
        selectCurrentOption,
    });

    const handleModalOpenChange = useAutocompleteModal({
        setShowDropdown,
        resetSelection,
        setOptions,
        setNoResults,
    });

    const onBlur = useAutocompleteBlur({
        isSmallScreen,
        getSelectedIndex: () => activeIndexRef.current,
        selectCurrentOption,
        setShowDropdown,
    });

    const wrappedHandleInputChange = useCallback(
        (value: string) => {
            setHasUserTyped(true);
            handleInputChange(value);
        },
        [handleInputChange],
    );

    const wrappedClearAll = useCallback(() => {
        setHasUserTyped(false);
        clearAll();
    }, [clearAll]);

    const renderField = ({ localId, opts = {} }: RenderFieldOptions) => (
        <>
            <AutocompleteInputField
                localId={localId}
                label={label}
                value={text}
                placeholder={placeholder}
                error={error}
                vertical={vertical}
                departure={departure}
                showDropdown={showDropdown}
                selectedOption={selectedOption}
                hasSelectedOption={options.length > 0 && activeIndex !== -1}
                selectedIndex={activeIndex}
                onKeyDown={onKeyDown}
                onBlur={onBlur}
                onChange={wrappedHandleInputChange}
                onFocus={() => setShowDropdown(true)}
                inputRef={opts?.inputRef}
                containerRef={opts?.containerRef}
                autoFocus={opts?.autoFocus}
                clearAll={wrappedClearAll}
            />
            <AutocompleteList
                localId={localId}
                visible={showOptions}
                options={options}
                selected={activeIndex}
                vertical={vertical}
                departure={departure}
                fetching={fetching}
                showLoader={showLoader}
                noResults={noResults}
                showDefaultOption={showDefaultOption}
                shouldShowOptionCount={shouldShowOptionCount}
                onSelect={selectOption}
                onHover={setHovered}
                onMouseLeave={clearHovered}
                tip={text.length === 0 ? tip : undefined}
                optionsTitle={text.length === 0 ? initialOptionsTitle : undefined}
            />
        </>
    );

    return (
        <>
            <FrontPageModal
                breakpoint="sm"
                title={placeholder}
                isModalOpen={showDropdown}
                showFooter={false}
                onOpenChange={handleModalOpenChange}
                trigger={{
                    text: replaceDashWithComma(text) || placeholder,
                    prefix: <AutocompleteIcon option={selectedOption} vertical={vertical} departure={departure} />,
                }}
                hideChevron
                error={error}>
                {renderField({
                    localId: `${id}-mobile`,
                    opts: { inputRef: mobileInputRef, containerRef: mobileInputContainerRef, autoFocus: true },
                })}
            </FrontPageModal>
            <div className={styles.autocomplete}>{renderField({ localId: `${id}-desktop` })}</div>
        </>
    );
};
